import { Axios } from "@/utils/axios-observable";
import { Observable } from "rxjs";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import { Paging, PagedListData } from "@/models";
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

const authTokenHeaderName = "Authorization";

export class ApiService {
  private readonly axios = Axios.create({
    baseURL: process.env.VUE_APP_API_PATH
  });
  private readonly fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  private readonly fileExtension = '.xlsx';

  constructor() {
    this.axios.interceptors.request.use(
      config => {
        config.headers["Content-Type"] = "application/json";
        if (config.method?.toUpperCase() === "GET") {
          config.headers["Cache-Control"] = "no-cache";
          config.headers["Pragma"] = "no-cache";
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    this.axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (
          error.response &&
          error.response.data &&
          error.response.data.location
        ) {
          window.location = error.response.data.location;
        } else {
          return Promise.reject(error);
        }
      }
    );
  }

  public getAsync<T>(resource: string): Observable<T> {
    return this.axios.get<T>(resource);
  }

  public getBlobAsync(resource: string): Observable<AxiosResponse> {
    const config: AxiosRequestConfig = {
      responseType: "blob"
    };
    return this.axios.get<AxiosResponse>(resource, config);
  }

  public getCountAsync(resource: string): Observable<number> {
    return this.axios.get<number>(resource);
  }

  public findByPagesAsync<T>(
    resource: string,
    data: Paging
  ): Observable<PagedListData<T>> {
    return this.axios.post<PagedListData<T>>(resource, data);
  }

  public findByTermAsync<T>(resource: string, params: any): Observable<T> {
    return this.axios.get<T>(resource, { params: params });
  }

  public createAsync<T>(resource: string, obj: T): Observable<T> {
    return this.axios.post<T>(`${resource}`, obj);
  }

  public updateAsync<T>(resouce: string, id: string, obj: T): Observable<T> {
    return this.axios.put<T>(`${resouce}/${id}`, obj);
  }

  public exportToExcel(data: any[], filename: string): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const wb: XLSX.WorkBook = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    this.saveExcelFile(excelBuffer, filename);
  }

  private saveExcelFile(buffer: any, filename: string): void {
    const data: Blob = new Blob([buffer], { type: this.fileType });
    FileSaver.saveAs(data, filename + this.fileExtension);
  }
}

const $api = new ApiService();
export default $api;
